import React, { useState, useEffect, useRef } from 'react';
import cx from 'classnames';
import Styles from '@pages/analytics/analytics.module.scss';
import DefectsAnalyticTable from '@pages/analytics/common/defectsAnalyticsTable';
import DefectsDonutChart from '@pages/common/charts/defects-donut-chart';
import { chartFontFamily, NO_DATA_FOUND, defectAnalyticsLabel } from '@common/ui-constants';
import { getDefectStatisticTableData, defectsDonutChartCustomTooltip } from '@pages/analytics/common/util';
import { isEmptyValue, scrollTop } from '@src/util/common_utils';
import { colors } from '@src/css_config/colorConstants';

const StatusStatistics = (props) => {
  let {
    getBugSummary,
    openStatusBugSummaryModal,
    closeBugSummaryModal,
    selectedDefectID,
    statusChartData,
    reqBody,
    callAllApiOnFilterChange,
  } = props;

  let [series, setSeries] = useState([0]);
  let [chartLabel, setChartLabel] = useState([]);
  let [seriesColor, setSeriesColor] = useState(['red']); // initial rendering color
  let [tableData, setTableData] = useState([]);
  let [tableContent, setTableContent] = useState('ALL');
  let [callOnViewAll, setCallOnViewAll] = useState(false);

  //? States for scroll APi :: START
  let [initTable, setInitTable] = useState({
    from: 0,
    to: 20,
  });

  const statusTableRef = useRef();
  const [statusTableScrollPos, setStatusTableScrollPos] = useState(false);
  const [stopScrollApiCall, setStopScrollApiCall] = useState(0); //? to stop scroll api on no-content

  let getStatusTableData = async (params = { from: initTable.from, to: initTable.to }) => {
    try {
      let paylodParams = {
        reqBody,
        from: params.from,
        to: params.to,
        tableName: 'Status',
        tableContent: params.tableContent || tableContent,
      };

      if (paylodParams.from === 0 && paylodParams.to === 20) {
        let Response = await getDefectStatisticTableData(paylodParams);
        setTableData(Response.defects);
        setStopScrollApiCall(Response.totalDefects);
        setCallOnViewAll(false); //? to reset viewAll API call state
      } else if (paylodParams.from !== 0 && paylodParams.to !== 20 && stopScrollApiCall > paylodParams.from) {
        let Response = await getDefectStatisticTableData(paylodParams);
        setTableData([...tableData, ...Response.defects]);
        setStopScrollApiCall(Response.totalDefects);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (statusTableScrollPos) {
      setInitTable({
        from: initTable.to,
        to: initTable.to + 10,
      });
    }
  }, [statusTableScrollPos]);

  useEffect(() => {
    if (initTable.from !== 0 && initTable.to !== 20) {
      getStatusTableData();
    }
  }, [initTable]);

  //? States for scroll APi :: END

  useEffect(() => {
    setSeries(statusChartData.series);
    setChartLabel(statusChartData.label);
    setSeriesColor(statusChartData.colorCode);
  }, [statusChartData]);

  // TODO ::NOTE:: when request body change based on side filters

  useEffect(() => {
    if (callAllApiOnFilterChange) {
      setTableContent('ALL');

      let params = {
        from: 0,
        to: 20,
        tableContent: 'ALL',
      };
      getStatusTableData(params);
    }
  }, [callAllApiOnFilterChange]);

  // TODO :: when chick on view All legend reset initTable value & scroll position :: START
  useEffect(() => {
    if (callOnViewAll) {
      setInitTable({
        from: 0,
        to: 20,
      });

      scrollTop(statusTableRef);

      let params = {
        from: 0,
        to: 20,
      };
      getStatusTableData(params);
    }
  }, [callOnViewAll]);

  // TODO :: when chick on view All legend reset initTable value & scroll position :: END

  let PriorityStatisticsColumn = React.useMemo(
    () => [
      {
        Header: 'BUG ID',
        accessor: 'defectId',
        width: '20%',
        Cell: ({ row, value }) => {
          return (
            <div
              className="overflow-hidden overflow-ellipsis whitespace-nowrap pr-3 rs-blue cursor-pointer"
              title={value}
              onClick={() => getBugSummary(value, 'priorityBugSummary')}
            >
              {value}
            </div>
          );
        },
      },
      {
        Header: 'SUMMARY',
        accessor: 'summary',
        width: '40%',
        Cell: ({ value }) => {
          return (
            <div className="overflow-hidden overflow-ellipsis whitespace-nowrap pr-3" title={value}>
              {value}
            </div>
          );
        },
      },
      {
        Header: 'STATUS',
        accessor: 'content',
        width: '20%',
        Cell: ({ value }) => {
          return (
            <div className="overflow-hidden overflow-ellipsis whitespace-nowrap pr-3" title={value}>
              {value}
            </div>
          );
        },
      },
      {
        Header: 'CREATED BY',
        accessor: 'createdBy',
        width: '20%',
        Cell: ({ value }) => {
          return (
            <div className="overflow-hidden overflow-ellipsis whitespace-nowrap pr-3" title={value}>
              {value}
            </div>
          );
        },
      },
    ],
    []
  );

  let handleClickNameByIndexAndApiCall = (index) => {
    setTableContent(chartLabel.at(index));
    scrollTop(statusTableRef);

    setStopScrollApiCall(true);

    let params = {
      reqBody,
      from: 0,
      to: 20,
      tableContent: chartLabel.at(index).toUpperCase(),
    };
    getStatusTableData(params);
  };

  let donutOptions = {
    series: [0, 0, 0],
    chart: {
      type: 'donut',
      events: {
        dataPointSelection: function (event, chartContext, config) {
          setInitTable({
            from: 0,
            to: 20,
          });
          if (isEmptyValue(config.selectedDataPoints[0])) {
            //NOTE :: if chart protion selected deselect call All API (Reset)
            setTableContent('ALL');
            scrollTop(statusTableRef);

            getStatusTableData();
          } else {
            handleClickNameByIndexAndApiCall(config.dataPointIndex);
          }
        },
      },
    },
    dataLabels: {
      enabled: false,
    },
    states: {
      hover: {
        filter: {
          type: 'none',
        },
      },
    },
    colors: seriesColor,
    labels: chartLabel,
    plotOptions: {
      bar: {
        borderRadius: 10,
      },
      pie: {
        donut: {
          labels: {
            show: true,
            value: {
              show: true,
              fontSize: '14px',
              fontFamily: chartFontFamily,
              fontWeight: 600,
            },
            total: {
              show: true,
              label: 'Total Defects',
              fontSize: '10px',
              fontFamily: chartFontFamily,
              fontWeight: 600,
              color: colors.black,
            },
          },
        },
      },
    },
    legend: {
      position: 'right',
      fontSize: 10,
      fontFamily: chartFontFamily,
      fontWeight: 500,
      formatter: (seriesName, options) => {
        return `${seriesName.split('/').at(-1).length > 5 ? seriesName.split('/').at(-1).slice(0, 5) + '..' : seriesName.split('/').at(-1)} : ${options.w.globals.series[options.seriesIndex]}`;
      },
      markers: {
        width: 11,
        height: 8,
        radius: 0,
      },
    },
    tooltip: {
      enabled: true,
      custom: defectsDonutChartCustomTooltip,
    },
  };

  return (
    <section className={cx(Styles['defectStatisticSection'])}>
      <article className={cx(Styles['leftGarpgBlock'])}>
        <div className="graphHeading ml-2 rs-blue uppercase fontPoppinsSemiboldSm pt-1">
          {' '}
          {defectAnalyticsLabel.label.status}
        </div>
        <DefectsDonutChart
          series={series}
          donutOptions={donutOptions}
          setTableContent={setTableContent}
          setCallOnViewAll={setCallOnViewAll}
          moduleVariable="status"
        />
      </article>
      <article className={cx(Styles['rightTableBlock'])}>
        <DefectsAnalyticTable
          setScrollPos={setStatusTableScrollPos}
          ref={statusTableRef}
          header={`Defects By Status- ${tableContent}`}
          columns={PriorityStatisticsColumn}
          data={tableData}
          message={NO_DATA_FOUND}
          openBugSummaryModal={openStatusBugSummaryModal}
          closeBugSummaryModal={closeBugSummaryModal}
          selectedDefectID={selectedDefectID}
        />
      </article>
    </section>
  );
};

export default StatusStatistics;
